import * as React from "react";
import { Quest } from "@/types/quests";
import { useState, useEffect } from "react";
import Direction from "@/components/atomics/direction";
import { useWallet } from "@/context/WalletContext";
import { QuestData, questsBackend, SessionRewardItemData } from "@/services/questsBackend";
import { ratingBackend } from "@/services/ratingBackend";
import QuestRewardsGrid from "./quest-rewards-grid";
import RewardSuccessModal from "./reward-success-modal";
import UnlockConfirmationModal from "./unlock-confirmation-modal";
import UnlockSuccessModal from "./unlock-success-modal";
import MotionModal from "@/components/atomics/motion-modal";
import LeftMotionModal from "@/components/atomics/left-motion-modal";
import GuidePopup from "@/components/atomics/guidePopup";
import { useSnackbar } from "notistack";
import { QUEST_STYLES, DEFAULT_QUEST_STYLE } from "@/constants/quests";
import { useTelegram } from "@/hooks/useTelegram";
import { handleExternalLinkClick } from "@/components/utils/navigation";
import { sleep } from "@/utils/common";

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
};

const getDateColor = (startDate: string, endDate: string | null): string => {
  if (!startDate) return "text-white";

  const now = new Date();
  const start = new Date(startDate);

  if (!endDate) {
    return now >= start ? "text-quest-green" : "text-white";
  }

  const end = new Date(endDate);

  if (now >= start && now <= end) {
    return "text-quest-green";
  }

  if (now > end) {
    return "text-primary-red";
  }

  return "text-white";
};

type RewardStatus = {
  type: "new_reward" | "next_reward" | "missed" | "finished" | "none";
  nextDateTime?: string;
};

const getRewardStatus = (
  sessions: SessionRewardItemData[],
  isClaimed: boolean,
  rewardsCount: number
): RewardStatus => {
  if (isClaimed) {
    return { type: "finished" };
  }

  const now = new Date();
  const dailySessions = sessions.filter(s => !s.is_bonus);

  // Check for available reward (New Reward)
  for (const session of dailySessions) {
    if (session.date_open && session.date_close) {
      const dateOpen = new Date(session.date_open);
      const dateClose = new Date(session.date_close);
      if (now >= dateOpen && now <= dateClose && !session.rewarded) {
        return { type: "new_reward" };
      }
    }
  }

  // Check for missed reward
  for (const session of dailySessions) {
    if (session.date_open && session.date_close) {
      const dateClose = new Date(session.date_close);
      if (now > dateClose && !session.rewarded) {
        return { type: "missed" };
      }
    }
  }

  // Check for next locked reward (Next Reward in time)
  for (const session of dailySessions) {
    if (session.date_open && session.date_close) {
      const dateOpen = new Date(session.date_open);
      if (now < dateOpen && !session.rewarded) {
        return { type: "next_reward", nextDateTime: session.date_open };
      }
    }
  }

  // No sessions yet but rewards exist - show new reward for first claim
  if (dailySessions.length === 0 && rewardsCount > 0) {
    return { type: "new_reward" };
  }

  return { type: "none" };
};

interface QuestCardProps {
  quest: Quest;
  onRequestWalletConnection?: () => void;
}

const formatCountdown = (targetDate: string): string => {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const diff = target - now;

  if (diff <= 0) return "00:00";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export default function QuestCard({ quest, onRequestWalletConnection }: QuestCardProps) {
  const [isClaimed, setIsClaimed] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [showXpCard, setShowXpCard] = useState<boolean>(false);
  const [sessions, setSessions] = useState<SessionRewardItemData[]>([]);
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [claimedRewardAmount, setClaimedRewardAmount] = useState<number>(0);
  const [previousExperience, setPreviousExperience] = useState<number>(0);
  const [isClaimedBonus, setIsClaimedBonus] = useState<boolean>(false);
  const [showUnlockConfirmation, setShowUnlockConfirmation] =
    useState<boolean>(false);
  const [showUnlockSuccess, setShowUnlockSuccess] = useState<boolean>(false);
  const [unlockData, setUnlockData] = useState<{
    cardIndex: number;
    cardType: "daily" | "bonus";
    cardLabel: string;
    sessionId?: number;
  } | null>(null);
  const [countdown, setCountdown] = useState<string>("");
  const [showGuidePopup, setShowGuidePopup] = useState<boolean>(false);
  const { walletAddress, isConnected } = useWallet();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isAuthenticated: isTelegramVerified, user: telegramUser } =
    useTelegram();

  const isCompletelyMissed =
    !!quest.date_end &&
    !quest.completed_by_user &&
    new Date(quest.date_end).getTime() < Date.now();

  // console.log("Quest complete info:", quest.id, quest.date_end, quest.completed_by_user, new Date(quest.date_end).getTime() < Date.now());
  const checkCompletion = async (quest: QuestData) => {
          const result = await questsBackend.checkQuestCompletion({
        questid: quest.id,
      });
      setIsCompleted(result.ok || false);
      setIsClaimed(result.isClaimed || false);
      await sleep(1100)
  }

  const isMiniApp = (() => {
    const tg = window.Telegram?.WebApp;
    return (
      typeof window !== "undefined" &&
      tg &&
      !!tg.initData &&
      tg.platform !== "unknown"
    );
  })();
  // console.log("Is success:", showUnlockSuccess);
  // Close toggled card when wallet connection changes
  useEffect(() => {
    setShowXpCard(false);
  }, [walletAddress, isConnected]);

  useEffect(() => {
    const loadQuestData = async () => {
      if (!isConnected || !walletAddress) {
        setIsCompleted(false);
        setIsClaimed(false);
        setSessions([]);
        return;
      }

      // Check completion status
      const result = await questsBackend.checkQuestCompletion({
        questid: quest.id,
      });
      setIsCompleted(result.ok || false);
      setIsClaimed(result.isClaimed || false);

      // Load sessions for status display
      try {
        const sessionsResult = await questsBackend.getQuestRewardSessions({
          questid: quest.id,
          wallet: walletAddress,
        });
        setSessions(sessionsResult.sessions || []);

        // console.log("sessionsResult", sessionsResult);
      } catch (error) {
        console.error("Error loading sessions:", error);
      }
    };
    loadQuestData();
  }, [quest, walletAddress, isConnected]);

  useEffect(() => {
    const rewardStatus = getRewardStatus(
      sessions,
      isClaimed,
      quest.rewards_count
    );

    if (rewardStatus.type === "next_reward" && rewardStatus.nextDateTime) {
      setCountdown(formatCountdown(rewardStatus.nextDateTime));

      const interval = setInterval(() => {
        const newCountdown = formatCountdown(rewardStatus.nextDateTime!);
        setCountdown(newCountdown);

        if (newCountdown === "00:00:00") {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [sessions, isClaimed, quest.rewards_count]);

  const questStyles =
    QUEST_STYLES[Number(quest.rarity_label) as keyof typeof QUEST_STYLES] ||
    DEFAULT_QUEST_STYLE;

  const handleToggleXpCard = async () => {
    // Refresh sessions when opening
    if (!showXpCard && walletAddress) {
      try {
        const result = await questsBackend.getQuestRewardSessions({
          questid: quest.id,
          wallet: walletAddress,
        });
        setSessions(result.sessions || []);
      } catch (error) {
        console.error("Error fetching quest reward sessions:", error);
      }
    }
    setShowXpCard(!showXpCard);
  };

  const handleClaimReward = async (
    cardIndex: number,
    cardType: "daily" | "bonus",
    sessionItemId: number,
    xpAmount: number
  ) => {
    // Check wallet connection FIRST
    if (!isConnected || !walletAddress) {
      if (onRequestWalletConnection) {
        onRequestWalletConnection();
      } else {
        enqueueSnackbar("Wallet is not connected", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
        });
      }
      return;
    }

    // Then check if quest is completed
    if (!isCompleted) {
      setShowGuidePopup(true);
      return;
    }

    if (isClaiming) return;

    setIsClaiming(true);
    try {
      if (walletAddress) {
        try {
          const ownerData =
            await ratingBackend.getOwnerRatingData(walletAddress);
          if (ownerData && ownerData.experience !== undefined) {
            setPreviousExperience(Number(ownerData.experience));
          }
        } catch (error) {
          console.error("Error fetching previous experience:", error);
        }
      }

      let result;

      if (quest.rewards_count === 1) {
        result = await questsBackend.completeAndClaim({
          questid: quest.id,
        });
      } else {
        if (sessions.length === 0) {
          await questsBackend.completeAndClaim({
            questid: quest.id,
          });
          result = await questsBackend.claimRewardInSession({
            questid: quest.id,
            sessionItemId: sessionItemId,
          });
        } else {
          result = await questsBackend.claimRewardInSession({
            questid: quest.id,
            sessionItemId: sessionItemId,
          });
        }
      }

      const isSuccess = result.ok !== false && !result.error;

      if (isSuccess) {
        const newSessions = await questsBackend.getQuestRewardSessions({
          questid: quest.id,
          wallet: walletAddress,
        });
        setSessions(newSessions.sessions);
        const currentReward = newSessions.sessions?.find(
          item => item.id === sessionItemId
        );
        setClaimedRewardAmount(result?.amount || currentReward?.reward || xpAmount);
        setIsClaimedBonus(cardType === "bonus");
        setShowSuccessModal(true);

        if (quest.rewards_count === 1) {
          console.log("Case 1:", result);
          setIsCompleted(true);
          setIsClaimed(true);
          setClaimedRewardAmount(result?.amount || xpAmount);
        } else {
          const completionResult = await questsBackend.checkQuestCompletion({
            questid: quest.id,
          });
          setIsCompleted(completionResult.ok || false);
          setIsClaimed(completionResult.isClaimed || false);
        }
      } else {
        enqueueSnackbar(
          result.error || "Failed to claim reward. Please try again.",
          {
            SnackbarProps: {
              onClick: () => {
                closeSnackbar();
              },
            },
            variant: "error",
          }
        );
      }
    } catch (error) {
      console.error("Error claiming reward:", error);
      enqueueSnackbar("Error claiming reward. Please try again.", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleUnlockReward = (
    cardIndex: number,
    cardType: "daily" | "bonus",
    cardLabel: string,
    sessionId?: number
  ) => {
    if (!isConnected || !walletAddress) {
      if (onRequestWalletConnection) {
        onRequestWalletConnection();
      } else {
        enqueueSnackbar("Wallet is not connected", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
        });
      }
      return;
    }
    setUnlockData({ cardIndex, cardType, cardLabel, sessionId });
    setShowUnlockConfirmation(true);
  };

  const handleConfirmUnlock = async () => {
    if (!unlockData || !walletAddress) return;

    // Check if user is verified by Telegram (only for web version)
    if (!isMiniApp && !isTelegramVerified) {
      enqueueSnackbar("You have to verify Telegram first", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
      });
      return;
    }

    setShowUnlockConfirmation(false);

    try {
      // Fetch previous experience before unlocking
      try {
        const ownerData = await ratingBackend.getOwnerRatingData(walletAddress);
        if (ownerData && ownerData.experience !== undefined) {
          setPreviousExperience(Number(ownerData.experience));
        }
      } catch (error) {
        console.error("Error fetching previous experience:", error);
      }

      const result = await questsBackend.initRewardUnblockInApp({
        itemid: unlockData.sessionId || 0,
        questid: quest.id,
        sessionItemId: unlockData.sessionId || 0,
        wallet: walletAddress,
        chatId: telegramUser?.id,
      });

      if (result.success) {
        const newSessions = await questsBackend.getQuestRewardSessions({
          questid: quest.id,
          wallet: walletAddress,
        });
        setSessions(newSessions.sessions);
        const currentReward = newSessions.sessions?.find(
          item => item.id === unlockData.sessionId
        );
        // Store reward amount for the success modal

        setClaimedRewardAmount(
          currentReward?.reward || quest.reward_amount || 0
        );
        setIsClaimedBonus(unlockData.cardType === "bonus");
        setShowUnlockSuccess(true);

        // Also update completion status
        const completionResult = await questsBackend.checkQuestCompletion({
          questid: quest.id,
        });
        setIsCompleted(completionResult.ok || false);
        setIsClaimed(completionResult.isClaimed || false);
      }
    } catch (error: any) {
      console.error("Error unlocking reward:", error);
      enqueueSnackbar(
        error?.error || "Failed to unlock reward. Please try again.",
        {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
        }
      );
    }
  };

  const handleCloseUnlockSuccess = () => {
    setShowUnlockSuccess(false);
    // Show the reward success modal after closing unlock success modal
    setShowSuccessModal(true);
    setUnlockData(null);
  };

  const hasCardBgImage =
    "cardBgImage" in questStyles && questStyles.cardBgImage;

  return (
    <div>
      <div
        className={`relative w-full flex aspect-[406/109] ${questStyles.cardBg}${isCompletelyMissed ? " disabled-quest" : ""}`}
        onClick={handleToggleXpCard}
        style={
          hasCardBgImage
            ? {
                backgroundImage: `url(${questStyles.cardBgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        {isCompleted && (
          <div className="absolute top-[6px] right-[5px] w-4 h-3">
            <img src="/assets/check-icon.svg" />
          </div>
        )}
        <div
          className={`relative w-[30%] flex align-center justify-center ${showXpCard ? "border-b-[3px] border-dark-navy-deep" : ""}`}
        >
          <div
            className={`absolute w-[calc(100%-16px)] aspect-square top-[-8px] border-[1px] ${questStyles.cardBorder}`}
            style={{
              backgroundImage: `url(${quest.icon || questStyles.iconBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="w-full h-full relative">
              <div
                className={`absolute left-1/2 -translate-x-1/2 bottom-[-8px] w-[43px] h-[16px] bg-black border-[1px] ${questStyles.cardBorder} flex items-center justify-center`}
              >
                <span className="text-[12px] text-primary-cyan">
                  {isClaimed
                    ? quest.rewards_count
                    : sessions.filter(s => !s.is_bonus && s.rewarded).length}
                  /{quest.rewards_count}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="w-[70%] flex flex-col">
          <div className="relative w-full h-[77%] flex">
            <div className="w-[75%] pt-[3px] flex">
              <span className="block text-[13px] text-white leading-[16px]">
                {quest.description}
              </span>
            </div>
            <div
              className="w-[25%] flex items-center justify-center cursor-pointer"
              onClick={handleExternalLinkClick(quest, checkCompletion)}
            >
              <img src="/assets/link.svg" className="w-[28px] h-[28px]" />
            </div>
            {quest.date_end && (
              <div className="absolute bottom-[4px] left-0 w-full flex gap-2">
                <span
                  className={`text-[12px] uppercase leading-none ${getDateColor(quest.date_start, quest.date_end)}`}
                >
                  start: {formatDate(quest.date_start)}
                </span>
                <span
                  className={`text-[12px] uppercase leading-none ${getDateColor(quest.date_start, quest.date_end)}`}
                >
                  end: {formatDate(quest.date_end)}
                </span>
              </div>
            )}
          </div>

          <div className="relative h-[23%] bg-dark-navy border-t-[3px] border-l-[3px] border-dark-navy-deep">
            {(() => {
              const rewardStatus = getRewardStatus(
                sessions,
                isClaimed,
                quest.rewards_count
              );

              if (rewardStatus.type === "finished") {
                return (
                  <div
                    className="absolute top-0 right-5 h-full flex items-center gap-2 cursor-pointer"
                    onClick={handleToggleXpCard}
                  >
                    <Direction
                      className={`w-[7px] h-[12px] transition-transform duration-300 ${
                        showXpCard ? "rotate-[270deg]" : "rotate-90"
                      }`}
                      color="#00ffff"
                    />
                    <span className="text-[16px] text-primary-cyan font-exo2 uppercase">
                      finished
                    </span>
                  </div>
                );
              }

              if (rewardStatus.type === "missed") {
                return (
                  <div
                    className="absolute top-0 right-5 h-full flex items-center gap-2 cursor-pointer"
                    onClick={handleToggleXpCard}
                  >
                    <Direction
                      className={`w-[7px] h-[12px] transition-transform duration-300 ${
                        showXpCard ? "rotate-[270deg]" : "rotate-90"
                      }`}
                      color="#ff0000"
                    />
                    <span className="text-[16px] text-primary-red font-exo2 uppercase">
                      missed
                    </span>
                  </div>
                );
              }

              if (rewardStatus.type === "next_reward") {
                return (
                  <div
                    className="absolute top-0 right-5 h-full flex items-center gap-2 cursor-pointer"
                    onClick={handleToggleXpCard}
                  >
                    <Direction
                      className={`w-[7px] h-[12px] transition-transform duration-300 ${
                        showXpCard ? "rotate-[270deg]" : "rotate-90"
                      }`}
                      color="#ff0000"
                    />
                    <span className="text-[16px] text-primary-red font-exo2 uppercase">
                      next reward in {countdown}
                    </span>
                  </div>
                );
              }

              // Check if quest is completely missed (disabled-quest)
              if (isCompletelyMissed) {
                return (
                  <div
                    className="absolute top-0 right-5 h-full flex items-center gap-2 cursor-pointer"
                    onClick={handleToggleXpCard}
                  >
                    <Direction
                      className={`w-[7px] h-[12px] transition-transform duration-300 ${
                        showXpCard ? "rotate-[270deg]" : "rotate-90"
                      }`}
                      color="#ff0000"
                    />
                    <span className="text-[16px] text-primary-red font-exo2 uppercase">
                      Missed
                    </span>
                  </div>
                );
              }

              // Default: new_reward or none
              return (
                <div
                  className="absolute top-0 right-5 h-full flex items-center gap-2 cursor-pointer"
                  onClick={handleToggleXpCard}
                >
                  <Direction
                    className={`w-[7px] h-[12px] transition-transform duration-300 ${
                      showXpCard ? "rotate-[270deg]" : "rotate-90"
                    }`}
                    color="#00ffff"
                  />
                  <span className="text-[16px] text-primary-cyan font-exo2 uppercase">
                    new reward
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      {showXpCard && (
        <QuestRewardsGrid
          questId={quest.id}
          rarityLabel={quest.rarity_label}
          rewardsCount={quest.rewards_count || 1}
          rewardAmount={quest.reward_amount || 0}
          sessions={sessions}
          isAllClaimed={isClaimed}
          onClaimReward={handleClaimReward}
          onUnlockReward={handleUnlockReward}
        />
      )}

      <MotionModal isOpen={showSuccessModal}>
        <RewardSuccessModal
          className="w-full bg-black"
          onClose={() => setShowSuccessModal(false)}
          rewardAmount={claimedRewardAmount}
          previousExperience={previousExperience}
          address={walletAddress || ""}
          isBonus={isClaimedBonus}
        />
      </MotionModal>

      <MotionModal isOpen={showUnlockConfirmation}>
        <UnlockConfirmationModal
          className="w-full bg-black"
          onClose={() => setShowUnlockConfirmation(false)}
          onUnlock={handleConfirmUnlock}
          rewardLabel={unlockData?.cardLabel || ""}
          starCost={quest.unblock_amount || 0}
        />
      </MotionModal>

      <MotionModal isOpen={showUnlockSuccess}>
        <UnlockSuccessModal
          className="w-full bg-black"
          onClose={handleCloseUnlockSuccess}
          rewardLabel={unlockData?.cardLabel || ""}
        />
      </MotionModal>

      <LeftMotionModal isOpen={showGuidePopup}>
        <div
          className="relative w-full h-full"
          onClick={() => setShowGuidePopup(false)}
        >
          <div className="absolute left-6 bottom-[calc(50%-66px)] md:left-[calc(50vw-200px)]">
            <GuidePopup title="">
              <div className="flex flex-col items-center">
                <span className="text-[15px] sl:text-[19px]">
                  FIRST, FINISH
                </span>
                <span className="text-[15px] sl:text-[19px]">THE TASK</span>
                <div className="flex items-center gap-2">
                  <span>CLICK ON</span>
                  <img
                    src="/assets/link.svg"
                    className="w-[19px] h-[19px] cursor-pointer"
                    alt="link"
                    onClick={handleExternalLinkClick(quest, checkCompletion)}
                  />
                </div>
              </div>
            </GuidePopup>
          </div>
        </div>
      </LeftMotionModal>
    </div>
  );
}
